import Button from '@orn/shared-web/components/Button/Button';
import * as Sentry from '@sentry/react';
import * as React from 'react';

interface IErrorBoundaryProps {
  children: React.ReactNode;
}

interface IErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Minimal top-level error boundary — captures render-time exceptions to Sentry
 * and renders a dense fallback panel. Keeps the spartan MOPS aesthetic: no card,
 * no illustration, just the failure mode and a retry.
 */
export default class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): IErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    Sentry.withScope((scope) => {
      scope.setExtras({ componentStack: errorInfo.componentStack });
      Sentry.captureException(error);
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-bg-primary text-fg-primary flex min-h-screen items-center justify-center p-8">
          <div className="border-border-primary max-w-lg border p-6 font-mono text-sm">
            <div className="mb-3 text-base font-semibold">Something broke.</div>
            <div className="text-fg-tertiary mb-4 whitespace-pre-wrap">
              {this.state.error?.message ?? 'Unknown error'}
            </div>
            <Button size="sm" color="secondary" onClick={this.handleReload}>
              Reload
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
